// shell.js v3
// Changes from v2:
//  - No categories region — Blockly's toolbox handles its own sidebar
//  - Single data-visible="true/false" attr replaces the dual
//    data-active-mobile / data-visible-desktop system
//  - .shell-main wrapper added so toolbar and main area are cleanly separated
//  - mountPaneToggle() builds a popover using your createPopover() API
//    instead of inline toggle pills. Can show Blocks, Code, or Both.
//  - Preview is fixed-size (CSS) — shell.js just mounts it, doesn't size it
import { isPageInList } from '@/scripts/helpers/pageUtils.js';
import { currLang } from '@/scripts/translation.js';

const DOWNLOAD_BUTTON_PAGES = [
  'art_factory',
  'functions',
  'js_factory',
  'nested_loops',
  'python_factory',
  'vernatics_esp',
  'vernatics_factory',
  'web_factory',
];
const compilerPages = ['pythoncompiler', 'jscompiler', 'sketch'];
const togglePage = ['factory', 'vernatics'];
const ICON_DOWNLOAD = `
<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
  <polyline points="7 10 12 15 17 10"/>
  <line x1="12" y1="15" x2="12" y2="3"/>
</svg>
`;

const REGIONS = ['blocks', 'code', 'preview'];

function createBtnTray(buttons, { justify } = {}) {
  const tray = document.createElement('div');
  tray.className = 'btn-tray';
  if (justify) tray.dataset.justify = justify;
  buttons.forEach((btn) => tray.appendChild(btn));
  return tray;
}

function buildShell(mode) {
  const shell = document.createElement('div');
  shell.id = 'app-shell';
  shell.dataset.mode = mode; // 'edit' | 'output-only'

  const slots = {};

  if (!isPageInList(compilerPages)) {
    const main = document.createElement('div');
    main.className = 'shell-main';

    const canvasGroup = document.createElement('div');
    canvasGroup.className = 'canvas-group';

    REGIONS.forEach((name) => {
      const slot = document.createElement('div');
      slot.className = 'shell-region';
      slot.dataset.regionSlot = name;
      slots[name] = slot;

      if (name === 'preview') {
        const previewColumn = document.createElement('div');
        previewColumn.className = 'preview-column';
        previewColumn.appendChild(slot);
        main.appendChild(previewColumn);
      } else {
        canvasGroup.appendChild(slot);
      }
    });

    main.appendChild(canvasGroup);
    shell.appendChild(main);
  }

  document.querySelectorAll('[data-region]').forEach((el) => {
    const region = el.dataset.region;
    if (slots[region]) {
      slots[region].appendChild(el);
    } else if (region === 'actions-extra') {
      slots._pendingExtraActions = slots._pendingExtraActions || [];
      slots._pendingExtraActions.push(el);
    }
  });

  if (slots.preview) {
    wrapPreviewInStageFrame(slots.preview);
  }

  document.body.prepend(shell);

  return {
    shell,
    main: shell.querySelector('.shell-main'),
    slots,
    canvasGroup: shell.querySelector('.canvas-group'),
  };
}

function wrapPreviewInStageFrame(previewSlot) {
  const original = previewSlot.querySelector('[data-region="preview"]');
  if (!original) return;
  const frame = document.createElement('div');
  frame.className = 'stage-frame';
  while (original.firstChild) frame.appendChild(original.firstChild);
  original.appendChild(frame);
}

// Code editor is NOT mounted by shell.js — your activity script owns this
// because CodeMirror mode (javascript/python/htmlmixed) varies per activity.
// Use AppShell.getCodeSlot() to get the container, then append your textarea
// there and call CodeMirror.fromTextArea on it.
// See usage pattern below in the public API.

// ── pane toggle popover ─────────────────────────────────────────────────
// Uses your createPopover() API. Imported as a module (ES import) or as
// a global if popover.js is loaded via a plain <script> tag.
// Call setPanes({ blocks, code }) to drive it externally too (e.g. if you
// want a keyboard shortcut).
let _setPanes;

const desktopMQ = window.matchMedia('(min-width: 800px)');
const shortHeightMQ = window.matchMedia('(max-height: 500px)');
function setupDownloadButton() {
  if (!isPageInList(DOWNLOAD_BUTTON_PAGES)) return;

  const toolbar = document.getElementById('toolbar');
  if (!toolbar) return;

  const download = document.createElement('button');
  download.id = 'download-button';
  download.classList.add('toolbar-btn');
  download.innerHTML = ICON_DOWNLOAD;

  toolbar.appendChild(download);
}

function mountPaneToggle(toolbar, createPopover) {
  if (!isPageInList(togglePage)) {
    return;
  }

  // Mobile: only Blocks or Code (not both at once)
  // Desktop: Blocks, Code, or Blocks + Code
  const ALL_MODES = [
    { id: 'blocks', label: currLang['UI.BlocksOnly'], blocks: true, code: false, desktopOnly: false },
    { id: 'code', label: currLang['UI.CodeOnly'], blocks: false, code: true, desktopOnly: false },
    { id: 'both', label: currLang['UI.BlocksAndCode'], blocks: true, code: true, desktopOnly: true },
  ];
  let activeMode = 'blocks';

  const trigger = document.createElement('button');
  trigger.className = 'pane-toggle-trigger toolbar-btn';
  trigger.setAttribute('aria-haspopup', 'true');
  trigger.innerHTML = `<text class="control-btn">${currLang['BlocksButton.Text']} ▾<i data-lucide="layout-grid"></i></text>`;

  const panel = document.createElement('div');
  panel.id = 'pane-toggle-panel';
  panel.className = 'popover-panel';
  panel.setAttribute('aria-hidden', 'true');

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:relative;display:inline-block;';
  wrapper.appendChild(trigger);
  wrapper.appendChild(panel);

  // insert before run button
  const runBtn = toolbar.querySelector('#run-button');
  toolbar.insertBefore(wrapper, runBtn);

  const optionEls = {};
  ALL_MODES.forEach(({ id, label }) => {
    const btn = document.createElement('button');
    btn.className = 'pane-option' + (id === activeMode ? ' active' : '');
    btn.dataset.mode = id;
    btn.innerHTML = `<span class="pane-option__check"></span>${label}`;
    btn.addEventListener('click', () => applyMode(id));
    panel.appendChild(btn);
    optionEls[id] = btn;
  });

  // Show/hide "Blocks + Code" option based on screen width
  function syncModeVisibility() {
    const isDesktop = desktopMQ.matches && !shortHeightMQ.matches;
    optionEls.both.style.display = isDesktop ? '' : 'none';
    // If currently in "both" mode and we've shrunk to mobile, fall back to blocks
    if (!isDesktop && activeMode === 'both') applyMode('blocks');
  }
  desktopMQ.addEventListener('change', syncModeVisibility);
  shortHeightMQ.addEventListener('change', syncModeVisibility);
  syncModeVisibility();

  const popover = createPopover({ trigger, panel });

  function applyMode(id) {
    activeMode = id;
    const mode = ALL_MODES.find((m) => m.id === id);
    trigger.innerHTML = `<text class="control-btn">${mode.label} ▾<i data-lucide="layout-grid"></i></text>`;
    Object.entries(optionEls).forEach(([k, el]) => el.classList.toggle('active', k === id));
    setPanesVisible({ blocks: mode.blocks, code: mode.code });
    popover.close();
    notifyLayoutChange();
  }

  _setPanes = (id) => applyMode(id);
  return { applyMode };
}

function setPanesVisible({ blocks, code }) {
  const b = document.querySelector('[data-region-slot="blocks"]');
  const c = document.querySelector('[data-region-slot="code"]');
  if (b) b.dataset.visible = blocks ? 'true' : 'false';
  if (c) c.dataset.visible = code ? 'true' : 'false';
}

function mountToolbar(shell, { extraButtons = [], createPopover } = {}) {
  const info = document.createElement('button');
  info.classList.add('instructions', 'instruct-button', 'toolbar-btn');
  info.textContent = currLang['UI.InfoButton'];

  const toolbar = document.createElement('div');
  toolbar.id = 'toolbar';
  toolbar.appendChild(info);
  [...(extraButtons || [])].forEach((b) => toolbar.appendChild(b));

  // Insert before shell-main
  const main = shell.querySelector('.shell-main');
  shell.insertBefore(toolbar, main);

  if (createPopover) {
    mountPaneToggle(toolbar, createPopover);
  }
  return toolbar;
}

function mountPreviewTray(slots, buttons, { label = currLang['UI.PreviewLabel'] } = {}) {
  const labelEl = document.createElement('div');
  labelEl.className = 'preview-label';
  labelEl.textContent = label;
  slots?.preview?.appendChild(labelEl);

  const tray = createBtnTray(buttons, { justify: 'between' });
  tray.classList.add('preview-tray');
  slots.preview?.appendChild(tray);
  return tray;
}

// ── layout-change notifications ─────────────────────────────────────────
const layoutListeners = [];

function notifyLayoutChange() {
  requestAnimationFrame(() => layoutListeners.forEach((fn) => fn()));
}

let resizeRaf = null;
window.addEventListener('resize', () => {
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(notifyLayoutChange);
});

// ── public API ──────────────────────────────────────────────────────────
window.AppShell = {
  // Load order matters:
  //   <script src="shell.js"></script>
  //   <script>AppShell.init({ mode: 'edit', createPopover });</script>
  //   <script type="module" src="scripts/music_factory.js"></script>
  //   In music_factory.js, after Blockly.inject():
  //     AppShell.registerBlocklyWorkspace(workspace, Blockly.svgResize);
  init({
    mode = 'edit',
    previewButtons = [],
    previewLabel = currLang['UI.PreviewLabel'],
    createPopover,
  } = {}) {
    // Always build the complete shell regardless of mode.
    // output-only just hides toolbar/canvas-group via CSS data-mode selector —
    // they're always in the DOM so boot.js can always find #toolbar after init().
    const { shell, slots } = buildShell(mode);
    mountToolbar(shell, {
      extraButtons: slots._pendingExtraActions || [],
      createPopover,
    });
    setupDownloadButton();
    mountPreviewTray(slots, previewButtons, { label: previewLabel });
    setPanesVisible({ blocks: true, code: false });
    notifyLayoutChange();
    return slots;
  },

  // Call this when the user clicks the edit button on a shared page.
  // Flips data-mode from output-only → edit, revealing toolbar and canvas.
  switchToEdit() {
    const shell = document.getElementById('app-shell');
    if (!shell || shell.dataset.mode === 'edit') return;
    shell.dataset.mode = 'edit';
    // canvas-group just went from display:none → visible.
    // Blockly measured 0×0 while hidden and won't self-correct —
    // notifyLayoutChange fires svgResize on the next frame after
    // the browser has applied the new layout.
    requestAnimationFrame(() => {
      notifyLayoutChange();
    });
  },

  setPanes(mode) {
    _setPanes?.(mode);
  },

  // Returns the code slot div — append your textarea here before calling
  // CodeMirror.fromTextArea, instead of appending to document.body.
  getCodeSlot() {
    return document.querySelector('[data-region-slot="code"]');
  },

  onLayoutChange(fn) {
    layoutListeners.push(fn);
    return () => {
      const i = layoutListeners.indexOf(fn);
      if (i !== -1) layoutListeners.splice(i, 1);
    };
  },

  registerBlocklyWorkspace(workspace, svgResizeFn) {
    if (!workspace) return () => {};
    const resolvedResize =
      svgResizeFn ||
      (window.Blockly &&
        typeof window.Blockly.svgResize === 'function' &&
        window.Blockly.svgResize);

    if (!resolvedResize) {
      /* ...same warning... */ return () => {};
    }

    const blocksSlot = document.querySelector('[data-region-slot="blocks"]');

    const resize = () => {
      if (blocksSlot && blocksSlot.offsetParent === null) return;
      try {
        resolvedResize(workspace); // Blockly.svgResize — resizes the <svg>
        workspace.resize(); // forces metrics + widget reposition (zoom/trash)
      } catch (err) {
        console.warn('[AppShell] svgResize failed:', err);
      }
    };

    // Instead of a single synchronous call, wait a couple of frames
    // so the initial flex/font layout has actually settled.
    requestAnimationFrame(() => requestAnimationFrame(resize));

    // Keep resyncing whenever the container's actual box size changes,
    // not just on window resize.
    let ro;
    if (blocksSlot && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => resize());
      ro.observe(blocksSlot);
    }

    const unsubscribe = this.onLayoutChange(resize);
    return () => {
      unsubscribe();
      ro?.disconnect();
    };
  },
};
